import { SystemHealthAPI } from '@API/data-services/system-health-service'
import { Routes } from '@Components/main-app/types'
import { LoadingRows } from '@Components/util/loading-rows'
import { LoyaltyRedemptionStatus } from '@Generated/back-office-client/src'
import { Badge, Flex, Heading, Select, Skeleton, Table, Text } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { formatInTimeZone } from 'date-fns-tz'
import { keyBy } from 'lodash'
import { ComponentProps } from 'react'
import { Link } from 'react-router-dom'
import { ErrorMessagesTable } from './error-messages-table'
import { HealthStats } from './health-stats'
import { HealthTimeline } from './health-timeline'
import {
  ALL_GAMES,
  FAILURE_RATE_ALERT_THRESHOLD,
  formatRate,
  HealthSearchParam,
  REFRESH_INTERVAL_MS,
} from './types'
import { useHealthSearchParams } from './use-health-search-params'

const statusColor = (status: LoyaltyRedemptionStatus): ComponentProps<typeof Badge>['color'] => {
  switch (status) {
    case LoyaltyRedemptionStatus.ERROR:
      return 'red'
    case LoyaltyRedemptionStatus.PENDING:
      return 'amber'
    default:
      return 'green'
  }
}

export const SpinHealthTab = ({
  windowHours,
  timezone,
}: {
  windowHours: number
  timezone: string
}) => {
  const { searchParams, setParam, currentHref } = useHealthSearchParams()
  const selectedGameId = searchParams.get(HealthSearchParam.GAME) ?? undefined

  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['spin-health', windowHours, selectedGameId],
    staleTime: 0,
    refetchInterval: REFRESH_INTERVAL_MS,

    queryFn: () => SystemHealthAPI.getSpinHealth(windowHours, selectedGameId),
  })

  const gamesById = keyBy(data?.games ?? [], 'id')

  return (
    <Flex direction="column" gap="4">
      <Flex gap="3" align="center">
        <Select.Root
          value={selectedGameId ?? ALL_GAMES}
          onValueChange={(value) =>
            setParam(HealthSearchParam.GAME, value === ALL_GAMES ? undefined : value)
          }>
          <Select.Trigger aria-label="Game" />
          <Select.Content>
            <Select.Item value={ALL_GAMES}>All games</Select.Item>
            {data?.games.map((game) => (
              <Select.Item key={game.id} value={game.id}>
                {game.name}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        {dataUpdatedAt ? (
          <Text size="2" color="gray">
            Updated {formatInTimeZone(dataUpdatedAt, timezone, 'p')}, refreshes every minute
          </Text>
        ) : null}
      </Flex>

      {isError ? <Text color="red">Error loading spin health</Text> : null}

      <HealthStats
        loading={isLoading}
        stats={[
          { label: 'Total', value: `${data?.total ?? 0}` },
          { label: 'Complete', value: `${data?.complete ?? 0}` },
          { label: 'Error', value: `${data?.error ?? 0}` },
          { label: 'Stuck pending', value: `${data?.stuckPending ?? 0}` },
          {
            label: 'Failure rate',
            value: formatRate(data?.failureRate ?? 0),
            alert: (data?.failureRate ?? 0) > FAILURE_RATE_ALERT_THRESHOLD,
          },
        ]}
      />

      {data ? (
        <HealthTimeline
          buckets={data.buckets}
          bucketMinutes={data.bucketMinutes}
          timezone={timezone}
          otherLabel="Other spins"
        />
      ) : null}

      <ErrorMessagesTable
        errorMessages={data?.errorMessages ?? []}
        timezone={timezone}
        loading={isLoading}
      />

      <Heading size="4">Recent spins</Heading>
      <Skeleton loading={isLoading}>
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Time</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Player</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Game</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Details</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              <LoadingRows />
            ) : (
              data?.recentSpins.map((spin) => (
                <Table.Row key={spin.id}>
                  <Table.Cell>{formatInTimeZone(spin.createdAt, timezone, 'PP p')}</Table.Cell>
                  <Table.Cell>
                    <Link
                      to={Routes.PLAYER_DETAILS_SPINS.replace(':lookupId', spin.playerLookupId)}
                      state={{ from: currentHref }}>
                      {spin.playerLookupId}
                    </Link>
                  </Table.Cell>
                  <Table.Cell>{gamesById[spin.gameId]?.name ?? spin.gameId}</Table.Cell>
                  <Table.Cell>
                    <Badge color={statusColor(spin.status)}>{spin.status}</Badge>
                  </Table.Cell>
                  <Table.Cell>{spin.details}</Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Skeleton>
    </Flex>
  )
}
